import {
  DropdownMenu,
  Hotkey,
  IconButton,
  type IconName,
} from '@wandb/components';
import {useEffect, useRef, useState} from 'react';

import {analyticsForShell} from '../analytics/segment';
import {beamerInit, openBeamer, useBeamerState} from './beamer';
import css from './ShellHeader.module.css';
import {useShellHeaderActions} from './ShellHeaderContext';

/** Mirrors the wandb app's `nav_right_feature_opened` so dashboards keep working. */
const trackFeatureOpened = (feature: string) =>
  analyticsForShell.track('nav_right_feature_opened', {feature});

export const AnnouncementsButton = () => {
  const {available, newPosts} = useBeamerState();

  useEffect(() => {
    void beamerInit();
  }, []);

  if (available === false) {
    return null;
  }
  return (
    <span className={css.badgeAnchor}>
      <IconButton
        aria-label="Announcements"
        icon="megaphone"
        size="md"
        variant="ghost"
        onPress={() => {
          trackFeatureOpened('whats_new');
          void openBeamer();
        }}
      />
      {newPosts > 0 && <span className={css.badge} aria-hidden="true" />}
    </span>
  );
};

type ResourceLink = {
  label: string;
  href: string;
  icon: IconName;
};

const LEARN_LINKS: ResourceLink[] = [
  {
    label: 'Forge documentation',
    href: 'https://docs.coreweave.com/forge-home',
    icon: 'documentation',
  },
];

const COMMUNITY_LINKS: ResourceLink[] = [
  {
    label: 'Blog',
    href: '/forge-reports',
    icon: 'blog-report',
  },
];

const ResourceItem = ({label, href, icon}: ResourceLink) => (
  <DropdownMenu.Item
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onAction={() => trackFeatureOpened(label)}>
    <DropdownMenu.ItemDecorationLeft iconProps={{icon}} />
    <DropdownMenu.ItemLabel>{label}</DropdownMenu.ItemLabel>
  </DropdownMenu.Item>
);

export const HelpResourcesMenu = () => {
  const {onKeyboardShortcutsPress} = useShellHeaderActions();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <IconButton
        ref={triggerRef}
        aria-label="Help resources"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        icon="help-alt"
        size="md"
        variant="ghost"
        onPress={() => {
          if (!isOpen) {
            trackFeatureOpened('resources');
          }
          setIsOpen(open => !open);
        }}
      />
      <DropdownMenu.Content
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        triggerRef={triggerRef}
        placement="bottom end"
        aria-label="Help resources">
        <DropdownMenu.Section>
          {LEARN_LINKS.map(link => (
            <ResourceItem key={link.label} {...link} />
          ))}
          {onKeyboardShortcutsPress != null && (
            <DropdownMenu.Item onAction={onKeyboardShortcutsPress}>
              <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'keyboard'}} />
              <DropdownMenu.ItemLabel>
                Keyboard shortcuts
              </DropdownMenu.ItemLabel>
              <Hotkey name="Keyboard shortcuts" keys={['Primary', 'Slash']} />
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Section>
        <DropdownMenu.Separator />
        <DropdownMenu.Section>
          {COMMUNITY_LINKS.map(link => (
            <ResourceItem key={link.label} {...link} />
          ))}
        </DropdownMenu.Section>
      </DropdownMenu.Content>
    </>
  );
};
